import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { Alert, Button, Space, Typography } from "antd";
import { useEffect, useState } from "react";
import { Dialog as ZhDialog, Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";

import { DEFAULT_VALUE } from "@/config";
import type { CustomerInfoQuery, CustomerInfoRecord } from "./CustomerInfoPage";

const memberLevelLabels: Record<number, string> = { 1: "非会员", 2: "基础会员", 3: "高级会员", 4: "企业会员" };
const userStateLabels: Record<number, string> = { 1: "正常", 2: "禁用", 3: "已注销" };

/** 将旧 Vue ProTable 的会员等级枚举转换为中文，未知值保持占位符。 */
function formatMemberLevel(value?: number | string): string {
    return memberLevelLabels[Number(value)] || DEFAULT_VALUE;
}

/** 将旧 Vue ProTable 的用户状态枚举转换为中文，未知值保持占位符。 */
function formatUserState(value?: number | string): string {
    return userStateLabels[Number(value)] || DEFAULT_VALUE;
}

export type CustomerSelectedQuery = Omit<CustomerInfoQuery, "page" | "size"> & {
    page?: number;
    size?: number;
    selectAll?: boolean;
    selectedIds?: number[];
    excludeIds?: number[];
};

export interface CustomerSelectedDialogProps {
    open: boolean;
    query: CustomerSelectedQuery;
    selectCount: number;
    communicateCount: number;
    loadCustomers: (query: CustomerSelectedQuery) => Promise<{ list?: CustomerInfoRecord[]; total?: number }>;
    onNavigate?: (id: number) => void;
    onCancel: () => void;
}

function getSelectedColumns(onNavigate?: (id: number) => void): TableColumnProps<CustomerInfoRecord>[] {
    return [
        {
            key: "id",
            label: "用户 ID",
            width: 180,
            prop: "customer",
            render: ({ row }) => (
                <Button type="link" onClick={() => onNavigate?.(row.customer.id)}>
                    {row.customer.code || row.customer.id}
                </Button>
            )
        },
        {
            key: "name",
            label: "个人实名/机构名称",
            minWidth: 160,
            prop: "customer",
            render: ({ row }) => row.customer.institutionName || row.customer.realName || DEFAULT_VALUE
        },
        {
            key: "mobile",
            label: "注册手机号",
            width: 140,
            prop: "customer",
            render: ({ row }) => row.customer.mobile || DEFAULT_VALUE
        },
        {
            key: "createdTime",
            label: "注册时间",
            width: 150,
            prop: "customer",
            render: ({ row }) => row.customer.createdTime || DEFAULT_VALUE
        },
        {
            key: "memberLevel",
            label: "会员等级",
            width: 150,
            prop: "customer",
            render: ({ row }) => formatMemberLevel(row.customer.memberLevel)
        },
        {
            key: "isConcat",
            label: "是否电联",
            width: 100,
            prop: "isConcat",
            render: ({ row }) => (row.isConcat ? "是" : "否")
        },
        {
            key: "summary",
            label: "最新沟通总结",
            width: 190,
            prop: "latestCommunicateSummary",
            render: ({ row }) => row.latestCommunicateSummary || DEFAULT_VALUE
        },
        {
            key: "state",
            label: "用户状态",
            width: 100,
            prop: "customer",
            render: ({ row }) => formatUserState(row.customer.state)
        }
    ];
}

/** 旧 Vue `viewSelectDataDialog` 的 React 迁移版，保留选中查询参数和无底部按钮配置。 */
export function CustomerSelectedDialog({
    open,
    query,
    selectCount,
    communicateCount,
    loadCustomers,
    onNavigate,
    onCancel
}: CustomerSelectedDialogProps) {
    const [rows, setRows] = useState<CustomerInfoRecord[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        if (!open) return;
        let disposed = false;
        void loadCustomers({ ...query, page, size: 10 })
            .then(result => {
                if (disposed) return;
                setRows(result.list || []);
                setTotal(result.total || 0);
                setErrorMessage("");
            })
            .catch(() => {
                if (disposed) return;
                setRows([]);
                setTotal(0);
                setErrorMessage("已选用户加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadCustomers, open, page, query]);

    const columns = getSelectedColumns(onNavigate);

    return (
        <ZhDialog open={open} title="查看已选" width={1200} footer={null} onCancel={onCancel} destroyOnHidden>
            <ZhTable<CustomerInfoRecord>
                data={rows}
                columns={columns}
                rowKey={row => String(row.customer.id)}
                emptyText="暂无已选用户"
                pagination={false}
                isShowResetBtn={false}
                isShowSearchBtn={false}
                tableHeader={
                    <div className="admin-table-composed-header">
                        <Space className="mb-4" wrap>
                            <Typography.Text>选中用户数：{selectCount}条</Typography.Text>
                            <Typography.Text>已选沟通记录数：{communicateCount}条</Typography.Text>
                        </Space>

                        {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}
                    </div>
                }
                append={
                    <div className="admin-table-composed-pagination">
                        <ZhPagination current={page} pageSize={20} total={total} hideOnSinglePage onChange={setPage} />
                    </div>
                }
            />
        </ZhDialog>
    );
}

export default CustomerSelectedDialog;
